import type { Shift } from "@ots-frontend/ots-data";
import { nextShiftDraft, type ShiftDraft, shiftDraftFromShift } from "../../shifts/shift-form";
import type { ActivityEntry } from "./activity-types";

const SECOND = 1_000;
const MINUTE = 60_000;

/** What a new or edited shift starts out with. Without an `id` it is a new shift. */
export type ActivityShiftDraft = ShiftDraft;

/** One side of a new ticket: when and where, as far as the selected rows tell. */
export type ActivityTransportDraft = {
  time?: number;
  siteId?: string;
  siteCompanyId?: string;
  containerId?: string;
};

export type ActivityTicketKind = "ticket" | "pickup" | "delivery";

/** What a new ticket, pickup, or delivery starts out with. */
export type ActivityTicketDraft = {
  kind: ActivityTicketKind;
  /** WATER or OIL. */
  category?: string;
  regionId?: string;
  personId?: string;
  vehicleId?: string;
  trailerId?: string;
  pickup?: ActivityTransportDraft;
  delivery?: ActivityTransportDraft;
};

type Equipment = Pick<ActivityShiftDraft, "regionId" | "personId" | "vehicleId" | "trailerId">;

/** Who drove what for an entry: its transport, else its geofence visit, site work, or shift. */
const equipmentOf = (entry: ActivityEntry): Equipment => {
  const transport = entry.pickup ?? entry.delivery;
  const source = transport ?? entry.geofenceEvent;
  return {
    regionId: transport?.regionId ?? entry.geofenceEvent?.regionId ?? entry.shift?.regionId ?? entry.containerVolumeDrop?.regionId,
    personId: entry.personId ?? source?.personId ?? entry.serviceWork?.personId ?? entry.shift?.personId,
    vehicleId: source?.vehicleId ?? entry.shift?.vehicleId,
    trailerId: source?.trailerId ?? entry.shift?.trailerId,
  };
};

/** The first entry's driver and equipment, with gaps filled from the rest. */
const sharedEquipment = (entries: ActivityEntry[]): Equipment => {
  const result: Equipment = {};
  for (const entry of entries) {
    const equipment = equipmentOf(entry);
    for (const key of Object.keys(equipment) as (keyof Equipment)[]) result[key] ??= equipment[key];
  }
  return result;
};

const span = (entries: ActivityEntry[]) => {
  const starts = entries.map((entry) => entry.time).filter((time): time is number => time != null);
  const ends = entries.map((entry) => entry.endTime ?? entry.time).filter((time): time is number => time != null);
  return { start: starts.length ? Math.min(...starts) : undefined, end: ends.length ? Math.max(...ends) : undefined };
};

const withoutUndefined = <T extends object>(value: T): T =>
  Object.fromEntries(Object.entries(value).filter(([, field]) => field !== undefined)) as T;

/**
 * A shift covering the selected rows, as WaterGo builds one from selected transports: the first row's driver
 * and equipment, from a second before the earliest to a second after the latest. An open shift is left open
 * when the rows run up to now.
 */
export const shiftDraftFromEntries = (entries: ActivityEntry[]): ActivityShiftDraft => {
  const { start, end } = span(entries);
  return withoutUndefined({
    ...sharedEquipment(entries),
    startDttm: start != null ? start - SECOND : undefined,
    endDttm: end != null && end !== start ? end + SECOND : undefined,
  });
};

/** The tickets' shift, when they all share one. */
export const sharedShift = (entries: ActivityEntry[]): Shift | undefined => {
  const shift = entries[0]?.shift;
  return shift && entries.every((entry) => entry.shift?.id === shift.id) ? shift : undefined;
};

const visitSide = (entry: ActivityEntry): ActivityTransportDraft | undefined => {
  const visit = entry.geofenceEvent;
  if (visit) return withoutUndefined({ time: visit.inDttm, siteId: visit.siteId, siteCompanyId: visit.siteCompanyId });
  const drop = entry.containerVolumeDrop;
  if (drop) return withoutUndefined({ time: drop.startDttm, siteId: drop.siteId, siteCompanyId: drop.siteCompanyId, containerId: drop.containerId });
  return undefined;
};

/**
 * A new ticket for what is selected. Geofence visits and tank drops say where it happened: two of them make the
 * pickup and the delivery, in time order, and one makes the side being added. Otherwise the ticket follows the
 * selection, a minute after the last of it, with the same driver, equipment, and freight category.
 */
export const ticketDraftFromEntries = (entries: ActivityEntry[], kind: ActivityTicketKind): ActivityTicketDraft => {
  const sorted = [...entries].sort((left, right) => (left.time ?? 0) - (right.time ?? 0));
  const sites = sorted.map(visitSide).filter((side): side is ActivityTransportDraft => !!side);
  const base: ActivityTicketDraft = withoutUndefined({
    kind,
    category: sorted.find((entry) => entry.category)?.category,
    ...sharedEquipment(sorted),
  });
  if (sites.length) {
    const [first, second] = sites;
    if (kind === "pickup") return { ...base, pickup: first };
    if (kind === "delivery") return { ...base, delivery: second ?? first };
    return { ...base, pickup: first, delivery: second ?? { time: first.time } };
  }
  const { end } = span(sorted);
  const time = end != null ? end + MINUTE : sorted[0]?.shift?.startDttm;
  return {
    ...base,
    pickup: kind === "delivery" ? undefined : withoutUndefined({ time }),
    delivery: kind === "pickup" ? undefined : withoutUndefined({ time }),
  };
};

// Shift drafts from a shift live with the shift form; kept here for the existing imports.
export { nextShiftDraft, shiftDraftFromShift };
